import React, { useEffect } from 'react';

import { LoadingBall } from '@depop/web-ui-kit/LoadingBall';
import { type DragEndEvent } from '@dnd-kit/core';
import clsx from 'clsx';

import { ShopErrorMessage } from '../ErrorMessage';

import styles from './styles.module.css';

import { ProductListCard } from '@/app/[locale]/shop/[username]/(shop)/components/ProductList/ProductListCard';
import { RearrangeProductList } from '@/app/[locale]/shop/[username]/(shop)/components/ProductList/RearrangeProductList';
import { InfiniteScroll } from '@/components/InfiniteScroll';
import {
  ActivityTrackerEventType,
  type ShopProfileSection,
} from '@/modules/activityTracker/constants.ts';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useActiveShopTab } from '@/modules/shop/hooks/useActiveShopTab';
import { useShopByUsername } from '@/modules/shop/hooks/useShopByUsername';
import { type ShopListProduct } from '@/modules/shop/types';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  EmptyState: React.FC;
  hasMore?: boolean;
  isError: boolean;
  isLoading: boolean;
  onEndReached: () => void;
  isFetchingNextPage: boolean;
  products?: ShopListProduct[];
  className?: string;
  isRearranging?: boolean;
  handleDragEnd?: (event: DragEndEvent) => void;
  activeTab?: ShopProfileSection;
};

export function ShopProductsList({
  EmptyState,
  hasMore,
  isError,
  isLoading,
  onEndReached,
  isFetchingNextPage,
  products,
  className,
  isRearranging = false,
  handleDragEnd = () => {
    return;
  },
  activeTab: activeTabProp,
}: Props) {
  const [_, currentUser] = useCurrentUser();
  const userId = currentUser?.id;
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId,
  });
  const { data } = useShopByUsername();
  const seller = data?.data;
  const { tab: tabFromUrl } = useActiveShopTab();
  const tab = activeTabProp ?? tabFromUrl;

  function handleEndReached() {
    sendActivityTrackerEvent(ActivityTrackerEventType.WEB_LOAD_MORE_ACTION, {
      sellerId: seller?.id,
      page: tab,
      schemaVersion: '1.0',
    });
    onEndReached();
  }

  useEffect(() => {
    sendActivityTrackerEvent(ActivityTrackerEventType.USER_PROFILE_VIEW, {
      userId: seller?.id,
      profileSection: tab,
      schemaVersion: '3.0',
    });
  }, [seller?.id, tab]);

  // i.e. fetching the first page of paginated results
  if (isLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <LoadingBall />
      </div>
    );
  }

  if (isError) {
    return <ShopErrorMessage />;
  }

  if (!products || !products.length) {
    return <EmptyState />;
  }

  return (
    <div className={clsx(styles.container, className)}>
      <InfiniteScroll
        onEndReached={handleEndReached}
        hasMore={hasMore}
        loading={isFetchingNextPage}
        LoadingComponent={<LoadingBall />}
      >
        <ol className={styles.productGrid}>
          {isRearranging ? (
            <RearrangeProductList
              products={products}
              handleDragEnd={handleDragEnd}
            />
          ) : (
            products.map((product) => (
              <li key={product.id} className={styles.listItem}>
                <ProductListCard product={product} activeTab={tab} />
              </li>
            ))
          )}
        </ol>
      </InfiniteScroll>
    </div>
  );
}
